import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { loadScript } from '@paypal/paypal-js';
import {
  catchError,
  debounceTime,
  firstValueFrom,
  lastValueFrom,
  map,
  Subject,
  take,
  takeUntil,
  throwError,
} from 'rxjs';
import { environment } from '../../../../../environment/environment';
import { InvoiceAddressBackendModel } from '../../../../models/address.model';
import { AlertService } from '../../../../services/alert.service';
import { CartService } from '../../../../services/cart.service';
import { CouponService } from '../../../../services/coupon.service';
import { UserService } from '../../../../services/user.service';
import { getCountryOptions } from '../../../../utils/address.utils';
import { fieldRequired } from '../../../../utils/form.utils';
import { houseNumberValidator } from '../../../../validators/house-number.validator';

@Component({
  selector: 'app-cart-invoice-formular',
  templateUrl: './cart-invoice-formular.component.html',
  styleUrl: './cart-invoice-formular.component.scss',
  standalone: false,
})
export class CartInvoiceFormularComponent implements OnDestroy {
  @Output() public boughtCouponAmount = new EventEmitter<number>();

  public isLoading = true;
  private paypalButtonsLoaded = false;

  public invoiceForm = new FormGroup({
    facility: new FormControl(''),
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    street: new FormControl('', Validators.required),
    houseNumber: new FormControl('', [
      Validators.required,
      houseNumberValidator(),
    ]),
    zipCode: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
    ]),
    city: new FormControl('', Validators.required),
    country: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    phone: new FormControl(''),
  });
  public countryOptions = [];

  public fieldRequired = fieldRequired;

  private destroy$ = new Subject<void>();

  constructor(
    public cartService: CartService,
    private userService: UserService,
    private couponService: CouponService,
    private router: Router,
    private alertService: AlertService
  ) {
    this.countryOptions = getCountryOptions();
    this.getCurrentUser();
    this.formChanges();
  }

  public ngAfterViewInit(): void {
    // Load PayPal button after component is initialized
    // this.loadPaypalButton();
  }

  private async getCurrentUser(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const userId = this.userService.currentUser$.value?.id;

      if (!userId) {
        resolve();
      }

      this.userService
        .getUserById(userId)
        .pipe(take(1))
        .subscribe({
          next: response => {
            const currentUser = response;

            this.invoiceForm.patchValue({
              firstName: currentUser.firstname,
              lastName: currentUser.lastname,
              street: currentUser.address?.street,
              houseNumber: currentUser.address?.houseNumber,
              zipCode: currentUser.address?.zipCode,
              city: currentUser.address?.city,
              country: currentUser.address?.country,
              email: currentUser.email,
            });
            this.isLoading = false;

            resolve();
          },
          error: error => {
            console.error('Error fetching user:', error);
            this.isLoading = false;
            reject();
          },
        });
    });
  }

  public onGoBack(): void {
    this.router.navigate([], { queryParams: { tab: 'cart' } });
  }

  private formChanges() {
    this.invoiceForm.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(100))
      .subscribe(() => {
        if (this.invoiceForm.valid) {
          if (this.paypalButtonsLoaded) {
            return;
          }
          // add a timeout to prevent the paypal button from being loaded multiple times
          this.loadPaypalButton();
        } else {
          this.paypalButtonsLoaded = false;
        }
      });
  }

  public async loadPaypalButton(): Promise<void> {
    if (
      this.cartService.getItems().length === 0 ||
      !this.userService.currentUser$.value
    ) {
      return;
    }

    loadScript({
      clientId: environment.paypal_client_id,
      currency: 'EUR',
    })
      .then((paypal: any) => {
        this.paypalButtonsLoaded = true;
        paypal
          .Buttons({
            fundingSource: paypal.FUNDING.PAYPAL,
            disabled: !this.invoiceForm.valid,
            style: {
              shape: 'rect',
              layout: 'vertical',
              color: 'silver',
              label: 'paypal',
            },
            createOrder: (data, actions) => {
              const amount = this.cartService.getTotalAmount();
              return lastValueFrom(
                this.couponService.createPayPalOrder(amount).pipe(
                  map(response => {
                    return response;
                  }),
                  catchError(error => {
                    return throwError(() => error);
                  })
                )
              )
                .then(response => {
                  return response.id; // Return the order ID received from your backend
                })
                .catch(error => {
                  console.error('Error creating PayPal order', error);
                  throw new Error(error.error ?? 'Error creating PayPal order');
                });
            },

            onApprove: async (data, actions) => {
              // Fetch coupons from the cart
              const coupons = this.cartService.getItems().map(item => ({
                id: item.product.id,
                amount: item.amount,
                price: item.product.payedAmount,
              }));

              const invoiceAddress: InvoiceAddressBackendModel = {
                facility: this.invoiceForm.get('facility').value,
                first_name: this.invoiceForm.get('firstName').value,
                last_name: this.invoiceForm.get('lastName').value,
                street: this.invoiceForm.get('street').value,
                house_number: this.invoiceForm.get('houseNumber').value,
                zip_code: this.invoiceForm.get('zipCode').value,
                city: this.invoiceForm.get('city').value,
                country: this.invoiceForm.get('country').value,
                email: this.invoiceForm.get('email').value,
                phone: this.invoiceForm.get('phone').value,
              };

              try {
                // Call the service to buy coupons using firstValueFrom
                await firstValueFrom(
                  this.couponService.buyCoupons(
                    coupons,
                    invoiceAddress,
                    this.cartService.getTotalAmount()
                  )
                );

                // Capture the PayPal order after coupons are bought
                return actions.order.capture().then(details => {
                  // Handle successful purchase
                  this.boughtCouponAmount.emit(
                    this.cartService.getItemsCount()
                  ); // Save the bought coupons
                  this.cartService.clearCart(); // Clear the cart

                  // Navigate to success page
                  this.router.navigate([], {
                    queryParams: { tab: 'success' },
                  });
                });
              } catch (error) {
                console.error('Error in processing:', error.message || error);

                this.alertService.showErrorAlert(
                  'Die Zahlung mit PayPal konnte nicht abgeschlossen werden.'
                );
              }
            },

            onCancel: () => {},

            onError: (err: any) => {
              // If there is an error with the PayPal button
              console.error('PayPal Button Error', err.message, err);

              this.alertService.showErrorAlert(
                'Bei der Zahlung mit PayPal ist ein Fehler aufgetreten.'
              );
            },
          })
          .render('#paypal-button-container')
          .catch(error => {
            console.error('failed to render the PayPal Buttons', error);
          });
      })
      .catch(error => {
        console.error('failed to load the PayPal JS SDK script', error);
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
